import * as React from 'react';
import { Input, Label, makeStyles, tokens, useId } from '@fluentui/react-components';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    swatch: {
        width: '40px',
        height: '32px',
        padding: '2px',
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground1,
        cursor: 'pointer',
        flexShrink: 0,
        '::-webkit-color-swatch-wrapper': { padding: 0 },
        '::-webkit-color-swatch': { border: 'none', borderRadius: tokens.borderRadiusSmall },
        ':focus-visible': {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
        },
    },
    hex: {
        fontFamily: tokens.fontFamilyMonospace,
        maxWidth: '120px',
    },
});

const HEX = /^#[0-9a-f]{6}$/i;

export interface IColorFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
}

/** Native colour picker paired with an editable hex input. */
const ColorField: React.FC<IColorFieldProps> = ({ label, value, onChange, disabled }) => {
    const styles = useStyles();
    const id = useId('color');
    const [text, setText] = React.useState(value);

    React.useEffect(() => { setText(value); }, [value]);

    const commit = (next: string): void => {
        const normalized = next.startsWith('#') ? next : `#${next}`;
        if (HEX.test(normalized)) onChange(normalized.toLowerCase());
        else setText(value);
    };

    return (
        <div className={styles.root}>
            <Label htmlFor={id}>{label}</Label>
            <div className={styles.row}>
                <input
                    id={id}
                    type="color"
                    className={styles.swatch}
                    value={HEX.test(value) ? value : '#000000'}
                    disabled={disabled}
                    onChange={(event) => onChange(event.target.value)}
                    aria-label={`${label}, палитра`}
                />
                <Input
                    className={styles.hex}
                    value={text}
                    disabled={disabled}
                    maxLength={7}
                    aria-label={`${label}, hex`}
                    onChange={(_, data) => setText(data.value)}
                    onBlur={() => commit(text)}
                    onKeyDown={(event) => { if (event.key === 'Enter') commit(text); }}
                />
            </div>
        </div>
    );
};

export default ColorField;
